import { useEffect, useState } from 'react';
import type { MetaFields } from '@dutchiesdk/ecommerce-extensions-sdk';
import { useRemoteCommerceComponents } from './remote-theme';
import { useBridgeDataInterface } from './bridge/bridge-data-interface';

/**
 * Hook to get meta fields from the remote theme's getStoreFrontMetaFields function
 * Returns the MetaFields object that can be used to render meta tags
 */
export const useMetaFields = (skipExtraction = false): MetaFields | null => {
  const [metaFields, setMetaFields] = useState<MetaFields | null>(null);
  const { remoteThemeModule } = useRemoteCommerceComponents();
  const dataBridgeData = useBridgeDataInterface();

  // Extract stable values to avoid infinite loops
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const getMetaFieldsFn = remoteThemeModule.default?.getStoreFrontMetaFields;

  useEffect(() => {
    const loadMetaFields = async (): Promise<void> => {
      if (!getMetaFieldsFn || skipExtraction) {
        setMetaFields(null);
        return;
      }

      try {
        const fields = await getMetaFieldsFn(dataBridgeData);
        setMetaFields(fields);
      } catch (error) {
        setMetaFields(null);
      }
    };

    void loadMetaFields();
    // Depend on pathname (changes with navigation) and function reference (changes with theme)
    // dataBridgeData is intentionally NOT in deps - it's a new object every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, getMetaFieldsFn, skipExtraction]);

  return metaFields;
};
